/**
 * The client's view of the session, and nothing else.
 *
 * **This module exists to break an import cycle, and that is its whole job.**
 *
 * `apiClient` has to end the session when the API answers 401. `SessionService` has to POST
 * `/session/extend` and `/session/invalidate`, which — like every other request the app makes —
 * belong on `apiClient`. Each importing the other is a cycle, so for as long as the session state
 * lived on `SessionService`, those two calls could not use the shared client and were written with
 * bare `axios.post` instead. That is not a stylistic footnote: it is why the keep-alive and logout
 * were the two requests carrying no `application-name` header when AUD-9 made it mandatory
 * server-side, i.e. why the near-outage was "every logged-in user" rather than one screen.
 *
 * The seam is that `apiClient` never needed `SessionService`. It needed `clearSession`, which is
 * pure `localStorage` bookkeeping and depends on nothing. So the STATE moves down here, to a leaf
 * module, and the arrows all point one way:
 *
 *     apiClient      ->  sessionStore
 *     SessionService ->  apiClient  ->  sessionStore
 *
 * **Keep this module import-free.** An import added here is how the cycle comes back — and the next
 * person to hit it will do what the last one did and reach for bare `axios`.
 *
 * `localStorage` is read through on every call rather than mirrored into a module variable. The
 * mirror is what a singleton naturally grows, and it has one real failure mode: a second tab (or
 * `apiClient`'s 401 handler, which is a different module) clears the session and this copy keeps
 * reporting the user as signed in until something 401s. Reading through costs a synchronous
 * `getItem` on a tick that already runs once a second.
 *
 * The token itself is NOT here and never has been: it lives in the httpOnly `mtc_session` cookie
 * that JS cannot read (see SessionService's header comment). What is stored is the expiry, so the
 * UI can render a countdown without a round-trip.
 */

/** Expiry of the current session, epoch ms. */
const EXPIRY_KEY = "sessionExpiry";

/**
 * A token key from builds that predate the httpOnly cookie. Removed on every clear so a stale
 * token cannot sit on the disk of a shared clinic workstation indefinitely.
 */
const LEGACY_TOKEN_KEY = "session";

/** Prefix of the external-provider registration drafts — see `useRegistrationDraft`. */
const REGISTRATION_DRAFT_PREFIX = "xp-reg-draft:";

/** Epoch ms at which the session expires, or null when there is no session. */
export function getSessionExpiry(): number | null {
  const saved = localStorage.getItem(EXPIRY_KEY);
  if (!saved) return null;
  const parsed = parseInt(saved, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function setSessionExpiry(expiresAtMs: number): void {
  localStorage.setItem(EXPIRY_KEY, String(expiresAtMs));
}

/**
 * Forget everything about the signed-in user that lives on this device.
 *
 * Called from three places, which is the point of it living here: the user signing out, the
 * inactivity handler, and `apiClient`'s 401 interceptor.
 */
export function clearSession(): void {
  localStorage.removeItem(EXPIRY_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);

  // An in-progress external-provider registration draft holds that provider's name, practice
  // address and license numbers. Signing out on a shared clinic workstation must not leave it
  // behind for the next person.
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith(REGISTRATION_DRAFT_PREFIX)) {
      localStorage.removeItem(key);
    }
  }
}
